import { useEchainProductStore } from '@hooks/states/useEchainProductStore';
import { useProductPageStore } from '@hooks/states/useProductPageStore';
import { AssociationType } from '@libs/associations';
import type { ProductAttribute, ProductAttributeValueAmountUnit } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';
import { getInitialSupportTray } from '@libs/echain';
import { useUniqueAssociatedProductsQuery } from '@product/ProductPage/PageLayouts/hooks/useUniqueAssociatedProductsQuery';
import { getFilteredSupportTrayProducts } from '@product/ProductTabs/tabs/ProductSelectionTab/getFilteredSupportTrayProducts';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getLocale } from '@libs/constants';

export const useSupportTraysSelection = () => {
  const router = useRouter();
  const locale = getLocale(router);
  const currentProduct = useProductPageStore(state => state.currentProduct);
  const hasUserDeselectedSupportTrays = useEchainProductStore(state => state.hasUserDeselectedSupportTrays);
  const selectedSupportTrayProduct = useEchainProductStore(state => state.selectedSupportTrayProduct);
  const selectedSupportTrayVariant = useEchainProductStore(state => state.selectedSupportTrayVariant);
  const setSelectedSupportTrayProduct = useEchainProductStore(state => state.setSelectedSupportTrayProduct);
  const setSelectedSupportTrayVariant = useEchainProductStore(state => state.setSelectedSupportTrayVariant);
  const clearSelectedSupportTray = useEchainProductStore(state => state.clearSelectedSupportTray);
  const addEchainPartsListItem = useEchainProductStore(state => state.addEchainPartsListItem);
  const eChainPartsList = useEchainProductStore(state => state.eChainPartsList);
  const [hasSupportTraySelectionsChanged, setHasSupportTraySelectionsChanged] = useState(false);
  const groupedSupportTrays = useEchainProductStore(state => state.groupedSupportTrays);
  const setGroupedSupportTraysForCurrentProduct = useEchainProductStore(
    state => state.setGroupedSupportTraysForCurrentProduct
  );

  const { data: uniqueAssociatedProducts, isLoading: isLoadingUniqueAssociatedProducts } =
    useUniqueAssociatedProductsQuery();

  useEffect(() => {
    if (isLoadingUniqueAssociatedProducts) {
      return;
    }

    setGroupedSupportTraysForCurrentProduct(
      uniqueAssociatedProducts ? getFilteredSupportTrayProducts(currentProduct, uniqueAssociatedProducts) : []
    );
  }, [
    isLoadingUniqueAssociatedProducts,
    currentProduct,
    uniqueAssociatedProducts,
    setGroupedSupportTraysForCurrentProduct,
  ]);

  useEffect(() => {
    if ((!groupedSupportTrays || groupedSupportTrays.length === 0) && selectedSupportTrayVariant) {
      clearSelectedSupportTray(currentProduct.id);
      setHasSupportTraySelectionsChanged(true);
      return;
    }

    if (
      groupedSupportTrays &&
      groupedSupportTrays.length > 0 &&
      selectedSupportTrayProduct &&
      selectedSupportTrayVariant
    ) {
      const foundSupportTrayProductFromSameType = groupedSupportTrays.find(
        supportTrayProduct => supportTrayProduct.key === selectedSupportTrayProduct.key
      );
      const foundSupportTrayVariantFromSameType = foundSupportTrayProductFromSameType?.products.find(
        supportTrayVariant => {
          const variantAmount = getAttributeValue<ProductAttributeValueAmountUnit>(
            supportTrayVariant.attributes?.lenght as ProductAttribute<ProductAttributeValueAmountUnit>
          )?.amount;
          const selectedVariantAmount = getAttributeValue<ProductAttributeValueAmountUnit>(
            selectedSupportTrayVariant.attributes?.lenght as ProductAttribute<ProductAttributeValueAmountUnit>
          )?.amount;
          const variantWallBracket = getAttributeValue<boolean>(
            supportTrayVariant?.attributes?.wall_bracket as ProductAttribute<boolean>
          );
          const selectedVariantWallBracket = getAttributeValue<boolean>(
            selectedSupportTrayVariant?.attributes?.wall_bracket as ProductAttribute<boolean>
          );
          return variantAmount === selectedVariantAmount && variantWallBracket === selectedVariantWallBracket;
        }
      );

      if (
        foundSupportTrayVariantFromSameType &&
        foundSupportTrayVariantFromSameType.id !== selectedSupportTrayVariant.id
      ) {
        setSelectedSupportTrayProduct(foundSupportTrayProductFromSameType);
        setSelectedSupportTrayVariant(foundSupportTrayVariantFromSameType);
        return;
      }

      const isSelectedSupportTrayAvailable = Boolean(foundSupportTrayVariantFromSameType);
      if (!isSelectedSupportTrayAvailable) {
        setSelectedSupportTrayVariant(getInitialSupportTray(groupedSupportTrays));
        setHasSupportTraySelectionsChanged(true);
      }
    }
  }, [
    groupedSupportTrays,
    currentProduct,
    hasUserDeselectedSupportTrays,
    clearSelectedSupportTray,
    setSelectedSupportTrayVariant,
    selectedSupportTrayProduct,
    selectedSupportTrayVariant,
    setSelectedSupportTrayProduct,
  ]);

  useEffect(() => {
    if (
      currentProduct &&
      selectedSupportTrayVariant?.id &&
      !eChainPartsList?.partsList.some(part => part.id === selectedSupportTrayVariant.id)
    ) {
      void addEchainPartsListItem(selectedSupportTrayVariant, locale, AssociationType.SUPPORT_TRAY);
    }
  }, [selectedSupportTrayVariant, addEchainPartsListItem, eChainPartsList, currentProduct, locale]);

  return {
    isProductSupportTraysAvailable: Array.isArray(groupedSupportTrays) && groupedSupportTrays?.length > 0,
    hasSupportTraySelectionsChanged,
    resetHasSupportTraySelectionsChanged: () => setHasSupportTraySelectionsChanged(false),
  };
};
